/**
 * GPX einlesen und auf eine handhabbare Punktzahl eindampfen.
 *
 * Eine Marathon-Aufzeichnung von Garmin oder Strava hat schnell mehrere
 * tausend Punkte im Sekundentakt. Für einen Korridor von einigen hundert
 * Metern ist das sinnlos genau — und der Server nimmt höchstens 500 Punkte.
 */

export type LatLng = [number, number];

export interface GpxResult {
  points: LatLng[];
  /** Wie viele Punkte in der Datei standen, vor dem Vereinfachen. */
  originalCount: number;
  name: string | null;
  /** Streckenlänge in Metern, grob aus den Punkten berechnet. */
  lengthMeters: number;
}

const MAX_POINTS = 400;

export function parseGpx(xml: string): GpxResult {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error('Die Datei ließ sich nicht lesen — ist das wirklich eine GPX-Datei?');
  }

  // Reihenfolge nach Nützlichkeit: aufgezeichnete Spur, geplante Route,
  // einzelne Wegpunkte.
  const nodes =
    pick(doc, 'trkpt') ?? pick(doc, 'rtept') ?? pick(doc, 'wpt');
  if (!nodes) throw new Error('In der Datei stecken keine Streckenpunkte.');

  const raw: LatLng[] = [];
  for (const node of nodes) {
    const lat = Number(node.getAttribute('lat'));
    const lng = Number(node.getAttribute('lon'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
    // Aufeinanderfolgende Dubletten (Pause im Track) überspringen.
    const prev = raw[raw.length - 1];
    if (prev && prev[0] === lat && prev[1] === lng) continue;
    raw.push([lat, lng]);
  }

  if (raw.length < 2) throw new Error('Die Datei enthält weniger als zwei brauchbare Punkte.');

  const name =
    doc.querySelector('trk > name')?.textContent?.trim() ||
    doc.querySelector('rte > name')?.textContent?.trim() ||
    doc.querySelector('metadata > name')?.textContent?.trim() ||
    null;

  return {
    points: simplifyRoute(raw, MAX_POINTS),
    originalCount: raw.length,
    name,
    lengthMeters: Math.round(routeLength(raw)),
  };
}

function pick(doc: Document, tag: string): Element[] | null {
  // getElementsByTagName statt querySelectorAll: GPX-Dateien bringen oft
  // Namensräume mit, an denen CSS-Selektoren scheitern.
  const found = [...doc.getElementsByTagName(tag)];
  return found.length ? found : null;
}

/* ------------------------------------------------------------ Geometrie */

const toRad = (d: number) => (d * Math.PI) / 180;

function metersBetween(a: LatLng, b: LatLng): number {
  const R = 6_371_000;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(toRad(a[0])) * Math.cos(toRad(b[0]));
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function routeLength(points: LatLng[]): number {
  let sum = 0;
  for (let i = 1; i < points.length; i++) sum += metersBetween(points[i - 1], points[i]);
  return sum;
}

/** Abstand eines Punktes zur Geraden durch a und b, in Metern (flach gerechnet). */
function perpendicular(p: LatLng, a: LatLng, b: LatLng): number {
  const mPerLat = 111_320;
  const mPerLng = 111_320 * Math.cos(toRad((a[0] + b[0]) / 2));
  const px = (p[1] - a[1]) * mPerLng;
  const py = (p[0] - a[0]) * mPerLat;
  const bx = (b[1] - a[1]) * mPerLng;
  const by = (b[0] - a[0]) * mPerLat;
  const lenSq = bx * bx + by * by;
  if (lenSq === 0) return Math.hypot(px, py);
  const t = Math.max(0, Math.min(1, (px * bx + py * by) / lenSq));
  return Math.hypot(px - t * bx, py - t * by);
}

/**
 * Douglas-Peucker: behält die Punkte, die die Form ausmachen, und wirft
 * alles weg, was näher als `tolerance` an der Verbindungslinie liegt.
 */
function douglasPeucker(points: LatLng[], tolerance: number): LatLng[] {
  if (points.length < 3) return points;
  let maxDist = 0;
  let index = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpendicular(points[i], points[0], points[points.length - 1]);
    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }
  if (maxDist <= tolerance) return [points[0], points[points.length - 1]];
  return [
    ...douglasPeucker(points.slice(0, index + 1), tolerance).slice(0, -1),
    ...douglasPeucker(points.slice(index), tolerance),
  ];
}

/** Toleranz so lange erhöhen, bis die Punktzahl passt. */
export function simplifyRoute(points: LatLng[], maxPoints = MAX_POINTS): LatLng[] {
  if (points.length <= maxPoints) return points;
  let tolerance = 5;
  let result = points;
  while (result.length > maxPoints && tolerance < 5000) {
    result = douglasPeucker(points, tolerance);
    tolerance *= 1.8;
  }
  // Sollte die Form extrem verwinkelt sein, hart ausdünnen.
  if (result.length > maxPoints) {
    const step = Math.ceil(result.length / maxPoints);
    const thinned = result.filter((_, i) => i % step === 0);
    if (thinned[thinned.length - 1] !== result[result.length - 1]) thinned.push(result[result.length - 1]);
    result = thinned;
  }
  return result;
}

export const formatKm = (meters: number) =>
  meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
